import GamepadIcon from '@mui/icons-material/Gamepad';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Box, Button, IconButton, Menu, MenuItem, Tooltip as MuiTooltip, Typography, Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import html2canvas from 'html2canvas';
import React, { forwardRef, memo, useCallback, useMemo, useRef } from 'react';
import {
	CartesianGrid,
	Label,
	Legend,
	Line,
	LineChart,
	ReferenceArea,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis
} from 'recharts';
import utc from 'dayjs/plugin/utc';
import strings from '../../Globalization/index';
import consts from '../../consts';
import { useEnsureRef, useSetState } from '../../hooks';
import util from '../../util/index';
import AsyncAutocomplete from './AsyncAutocomplete';
import { getColors } from './charts/colors';

dayjs.extend(utc);

const getAxisYDomain = ({ from, to, ref, key, offset, data }) => {
	const startIndex = data.findIndex(entry => entry[key] === from);
	const endIndex = data.findIndex((entry) => entry[key] === to);
	const refData = data.slice(startIndex, endIndex);
	let [bottom, top] = [refData[0][ref], refData[refData.length - 1][ref]];
	refData.forEach((d) => {
		if (d[ref] > top) top = d[ref];
		if (d[ref] < bottom) bottom = d[ref];
	});
	top = (top | 0) - (offset | 0);
	bottom = (bottom | 0) + (offset | 0);
	return [top, bottom];
};

const initialState = {
	left: 'dataMin',
	right: 'dataMax',
	refAreaLeft: '',
	refAreaRight: '',
	domains: [['dataMax + 1', 'dataMin + 1']],
	top: 'dataMax+1',
	bottom: 'dataMin-1',
	top2: 'dataMax+20',
	bottom2: 'dataMin-20',
	animation: true,
	seriesList: {},
	data: [],
	menuAnchorEl: null,
	xAxisBoundaries: ['dataMin + 1', 'dataMax + 1'],
	yAxisBoundaries: ['auto', 'auto'],
	rectBoundaries: {},
	inactiveSeries: []
};

const CustomTooltipWrapper = ({ title, children }) => (
	<MuiTooltip
		title={title}
		PopperProps={{
			sx: {
				'.MuiTooltip-tooltip': {
					backgroundColor: 'white',
					color: 'black',
					border: '1px solid #ddd',
					borderRadius: '4px',
					fontSize: '14px',
					fontWeight: '600',
					padding: '10px 15px',
				},
			},
		}}
	>
		{children}
	</MuiTooltip>
);

const customizeLegend = seriesList => (props) => {
	const { payload, onClick, onMouseEnter, onMouseLeave } = props;
	return (
		<Box style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center' }}>
			{payload.map((entry, index) => {
				const seriesData = seriesList[entry.value];
				const description = seriesData ? seriesData.Description : 'No description available';

				return (
					<Box
						key={index}
						marginLeft={5}
					>
						<CustomTooltipWrapper title={description}>
							<Typography component="span" style={{ margin: '0px 5px', color: entry.color }} onMouseEnter={() => onMouseEnter && onMouseEnter(entry, index)} onMouseLeave={() => onMouseLeave && onMouseLeave(entry, index)}>
								{entry.value}
							</Typography>
						</CustomTooltipWrapper>
					</Box>
				);
			})}
		</Box>
	);
};

const StockChartGraph = forwardRef(
	(
		{
			openChartPopUp,
			data,
			chartDetail,
			trendsLinesConfigs,
			height,
			width,
			filters = [],
			hideOpenInNewWindowBtn,
			onPhasesFilter,
			trendlinesColor,
			extraControls = [],
			...rest
		},
		ref
	) => {
		const componentRef = useEnsureRef(ref);
		const zoomedInRef = useRef(false);
		const actionRef = useRef();
		const activeTooltipIndexRef = useRef(-1);
		const theme = useTheme();
		const exportComponentAsImage = async (ext = 'png') => {
			const element = componentRef.current;
			const canvas = await html2canvas(element);

			const data = canvas.toDataURL(`image/${ext}`);
			const link = document.createElement('a');

			if (typeof link.download === 'string') {
				link.href = data;
				link.download = `chart.${ext}`;

				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			} else {
				window.open(data);
			}
		}
		const exportComponentAsPDF = () => { };
		const exportComponentAsPNG = () => { };
		const handleLegendOnClick = (e) => {
			const value = e?.value;

			setState((prevState) => {
				const inactiveSeries = [...prevState.inactiveSeries];
				const seriesIndex = inactiveSeries.indexOf(value);
				if (seriesIndex !== -1) {
					inactiveSeries.splice(seriesIndex, 1);
				} else {
					inactiveSeries.push(value);
				}
				return {
					inactiveSeries
				};
			});
		};

		const handleLegendOnMouseEnter = (e) => {
			setState({
				selectedDataKey: e?.value
			});
		};

		const handleLegendOnMouseLeave = (e) => {
			setState({
				selectedDataKey: undefined
			});
		};

		const { options, seriesList, filterValue, filterOptions } = useMemo(() => {
			let phaseData, eventData;
			({ PhaseData: phaseData, Data: eventData } = data || {});
			if (!eventData && !phaseData) {
				eventData = [];
				phaseData = [];
			}
			for (const eventEntry of eventData) {
				eventEntry.EventTime = util.fixDates(eventEntry.EventTime);
			}
			const filterOptions = [];
			const filterValue = [];
			const isFiltered = filters?.length > 0;
			let filteredPhases = isFiltered ? [] : phaseData;
			for (const [index, phase] of phaseData.entries()) {
				phase.StartTime = util.fixDates(phase.StartTime);
				phase.EndTime = util.fixDates(phase.EndTime);
				filterOptions.push({ id: index, label: phase.PhaseName, PhaseName: phase.PhaseName });
				if (isFiltered && filters.find((o) => o.id === index)) {
					filteredPhases.push({ ...phase, index });
					filterValue.push(index);
				}
			}
			const units = chartDetail?.EQUIPMENTDETAIL?.HMIUnits;
			const items = [];
			if (isFiltered) {
				eventData = eventData.filter(
					(o) =>
						!!filteredPhases.find(
							(phase) =>
								(dayjs(phase.StartTime).isBefore(o.EventTime) ||
									dayjs(phase.StartTime).isSame(o.EventTime)) &&
								dayjs(phase.EndTime).isAfter(o.EventTime)
						)
				);
			}
			for (const value of filteredPhases) {
				const valuePhaseName = value.PhaseName,
					phaseName =
						strings[valuePhaseName] || strings[valuePhaseName?.toUpperCase()]
							? util.getTitleInUpperCase(strings[valuePhaseName] || strings[valuePhaseName?.toUpperCase()])
							: util.getTitleInUpperCase(valuePhaseName);
				items.push({
					value: value.StartTime,
					color: 'black',
					labelFontColor: 'black',
					labelPlacement: 'inside', //"outside"
					labelAlign: 'far', //"center", "near"
					label: phaseName,
					showOnTop: true,
					thickness: 2
				});
			}
			let options = {
				width: width,
				height: height,
				zoomEnabled: true,
				zoomType: 'xy',
				animationEnabled: true,
				exportEnabled: true,
				theme: 'light1', // "light2", "dark1", "dark2"
				axisX: {
					valueParser: (val) => new Date(val).getTime(),
					valueFormatter: (val) => dayjs(val).format(consts.MomentFormat.TimeFormat),
					stripLines: items
				},
				axisY: {
					title: units && units.Pressure,
					stripLines: []
				},
				axisY2: {
					title: units && units.Temperature,
					stripLines: []
				},
				legend: {
					cursor: 'pointer'
				},
				data: []
			};
			for (let k = 0; trendsLinesConfigs && k < trendsLinesConfigs?.line?.length; k++) {
				const element = trendsLinesConfigs.line[k];
				const trendObj = {
					labelFontColor: element.color,
					labelPlacement: 'outside',
					labelAlign: 'far',
					labelBackgroundColor: 'black',
					color: element.color,
					value: element.startvalue,
					label: element.toolText,
					showOnTop: element.showOnTop,
					thickness: 2
				};
				if (element.parentYAxis.toLowerCase() === 'p') {
					options.axisY.stripLines.push(trendObj);
				} else {
					options.axisY2.stripLines.push(trendObj);
				}
			}
			const seriesList = {};

			options.data = eventData.map((_data) => {
				const _tempObj = {
					eventName:
						typeof options?.axisX?.valueParser === 'function'
							? options.axisX.valueParser(_data.EventTime)
							: _data.EventTime
				};
				_data.SeriesData.forEach((_) => {
					const { SeriesName, Description } = _;

					// Use the SeriesName as the key, and value as an object
					Object.assign(seriesList, { [SeriesName]: { SeriesName, Description } });
					Object.assign(_tempObj, { [_.SeriesName]: _.Value });
				});
				return _tempObj;
			});
			return { options, seriesList, filterValue, filterOptions };
		}, [data, chartDetail, trendsLinesConfigs, filters]);

		const [state, setState] = useSetState({ ...initialState, data: options.data });
		const CustomizedLegend = useMemo(() => customizeLegend(seriesList), [seriesList]);


		const handleOnChangePhaseFilter = (event, newValue) => {
			if (typeof onPhasesFilter === 'function') {
				onPhasesFilter(newValue);
			}
		};

		const handleExportComponentAsJPEG = () => {
			exportComponentAsImage('jpg');

			setState({
				menuAnchorEl: null
			});
		};

		const printChart = () => {
			const domElem = componentRef.current;
			const content = `<html><head><style>body{background-color: unset !important;}</style></head><body>${domElem?.innerHTML}</body></html>`;
			setState({
				menuAnchorEl: null
			});
			const printIfmElem = document.createElement('iframe');
			document.body.appendChild(printIfmElem);
			const pri = printIfmElem?.contentWindow;
			pri.document.open();
			pri.document.write(content);
			pri.document.close();
			pri.focus();
			pri.onafterprint = () => {
				document.body.removeChild(printIfmElem);
			};
			pri.print();

			return true;
		};

		const handleExportComponentAsPNG = () => {
			setState({
				menuAnchorEl: null
			});
			exportComponentAsImage('png');

		};

		const handleOpenMenu = (event) => {
			setState({
				menuAnchorEl: event.currentTarget
			});
		};

		const handleCloseMenu = () => {
			setState({
				menuAnchorEl: null
			});
		};

		const zoom = ({ x1, x2, y1, y2, left, right }) => {
			const { data } = state;
			const reverse = x1 > x2;
			const xAxisBoundaries = reverse ? [right, left] : [left, right];
			const yAxisBoundaries = [y2, y1];

			const seriesKeys = typeof seriesList === 'object' ? Object.keys(seriesList) : [];
			const domains = [];
			// yAxis domains
			try {
				for (const seriesKey of seriesKeys) {
					const domain = getAxisYDomain({
						from: reverse ? right : left,
						to: reverse ? left : right,
						ref: seriesKey,
						key: 'eventName',
						offset: 50,
						data: options.data
					});
					domains.push(domain);
				}
			} catch (error) {
				console.error('zoom error', error);
			}
			if (domains.length > 0) {
				zoomedInRef.current = true;
			}
			const startIndex = options.data.findIndex((item) => left === item.eventName);
			const endIndex = options.data.findIndex((item) => right === item.eventName);
			setState(() => ({
				data: data.slice(),
				xAxisBoundaries,
				yAxisBoundaries,
				domains,
				panSize: Math.round(Math.abs(startIndex - endIndex) / 2)
			}));
		};

		const zoomOut = () => {
			zoomedInRef.current = false;
			setState(({ data }) => ({
				data: data.slice(),
				domains: [['dataMax+1', 'dataMin-1']],
				refAreaLeft: '',
				refAreaRight: '',
				left: 'dataMin',
				right: 'dataMax',
				top: 'dataMax+1',
				top2: 'dataMax+50',
				bottom: 'dataMin',
				xAxisBoundaries: ['dataMin + 1', 'dataMax+1'],
				yAxisBoundaries: ['auto', 'auto'],
				rectBoundaries: {},
				referenceArea: {},
				zoomedin: false,
				panning: false,
				panSize: 0
			}));
			actionRef.current = 'zoomout';
		};

		const handleOnChartMouseDown = (event) => {
			if (event?.activeLabel) {
				const { activeLabel, chartX, chartY, activeTooltipIndex } = event;
				activeTooltipIndexRef.current = activeTooltipIndex;
				zoomedInRef.current = false;
				actionRef.current = 'highlight';
				setState({
					refAreaLeft: activeLabel,
					rectBoundaries: { x1: chartX, y1: chartY },
					referenceArea: { x1: activeLabel, x2: activeLabel },
					left: activeLabel,
					activeTooltipIndex
				});
			}
		};

		const handleOnChartMouseUp = useCallback((event) => {
			if (event?.activeLabel) {
				actionRef.current = 'none';
				const { activeLabel, chartX, chartY, activePayload, activeTooltipIndex } = event;
				const { rectBoundaries, left } = state;
				const payloadValue = activePayload?.[0]?.value;
				const shouldZoom = state.activeTooltipIndex !== activeTooltipIndex;

				if (shouldZoom) {
					setState((prevState) => ({
						refAreaRight: activeLabel,
						rectBoundaries: { ...prevState.rectBoundaries, x2: chartX, y2: payloadValue },
						referenceArea: { ...prevState.referenceArea, x2: activeLabel },
						right: activeLabel,
						zoomedin: prevState.zoomedin || shouldZoom
					}));
					if (shouldZoom) {
						zoom({ ...rectBoundaries, x2: chartX, y2: chartY, left, right: activeLabel });
					}
				}

			}
		}, [state.activeTooltipIndex]);

		const handleOnChartMouseMove = useCallback(
			(event) => {
				if (event?.activeLabel) {
					const { activeLabel, chartX, chartY, activeTooltipIndex } = event;
					const { rectBoundaries, referenceArea, xAxisBoundaries, panSize } = state;

					if (state.panning && Math.abs(activeTooltipIndexRef.current - activeTooltipIndex) > 1) {
						let horizontalDirection = activeTooltipIndexRef.current > activeTooltipIndex ? 'left' : 'right';
						const boundaryLeftEventName = options.data[activeTooltipIndex - panSize]?.eventName;
						const boundaryRightEventName = options.data[activeTooltipIndex + panSize]?.eventName || activeLabel;
						let newXAxisBoundaries = [...xAxisBoundaries];
						const newReferenceArea =
							horizontalDirection === 'right'
								? { ...referenceArea, x2: activeLabel }
								: { ...referenceArea, x1: activeLabel };
						let newRectBoundaries =
							horizontalDirection === 'right'
								? { x1: rectBoundaries.x2, y1: rectBoundaries.y2, x2: chartX, y2: chartY }
								: { x2: rectBoundaries.x1, y2: rectBoundaries.y1, x1: chartX, y1: chartY };
						newXAxisBoundaries[0] = boundaryLeftEventName;
						newXAxisBoundaries[1] = boundaryRightEventName;
						const rectWidth = Math.abs(rectBoundaries.x2 - rectBoundaries.x1);
						const edgeX = horizontalDirection === 'right' ? rectBoundaries.x2 : rectBoundaries.x1;
						const shouldPan =
							boundaryLeftEventName && boundaryRightEventName && Math.abs(chartX - edgeX) / rectWidth > 0.1;
						if (shouldPan) {
							setState({
								xAxisBoundaries: newXAxisBoundaries,
								rectBoundaries: newRectBoundaries,
								referenceArea: { ...referenceArea, x2: activeLabel }
							});
						}
					} else if (actionRef.current === 'highlight') {
						setState((prevState) => ({
							referenceArea: {
								...prevState.referenceArea,
								x2: activeLabel
							},
							rectBoundaries: { ...prevState.rectBoundaries, x2: chartX, y2: chartY }
						}));
					}
					activeTooltipIndexRef.current = activeTooltipIndex;
				}
			},
			[state.panning, state.xAxisBoundaries, state.panSize, state.referenceArea]
		);

		const handleTogglePan = useCallback(() => {
			setState(prevState => ({ panning: !prevState.panning }));
		}, []);



		const chartColors = useMemo(() => getColors(Object.keys(seriesList)?.length || 0), [seriesList]);

		return (
			<Grid container flexDirection="column">
				<Grid size={12} container alignItems="center" paddingTop={2} paddingBottom={2}>
					<Grid size={{xs: 12, md: 5, lg: 6}} padding={1}>
						{filterOptions.length > 0 && (
							<AsyncAutocomplete
								value={filterValue}
								label={strings.SelectPhase}
								name="phase"
								size="small"
								margin="dense"
								multiple
								onChange={handleOnChangePhaseFilter}
								options={filterOptions}
								disablePortal
							/>
						)}
					</Grid>
					<Grid size={{xs: 12, md: 7, lg: 6}}>
						<Box display="flex" columnGap={1} alignItems="center" justifyContent={'flex-end'}>
							{state.zoomedin && (
								<IconButton
									color={state.panning ? 'primary' : 'textPrimary'}
									size="small"
									onClick={handleTogglePan}
									title={strings.Pan}
								>
									<GamepadIcon />
								</IconButton>
							)}
							{state.zoomedin && (
								<IconButton color="primary" size="small" onClick={zoomOut} title={strings.ResetChart}>
									<RestartAltIcon />
								</IconButton>
							)}
							<IconButton color="textPrimary" size="small" onClick={handleOpenMenu}>
								<MoreVertIcon />
							</IconButton>
							<Menu
								disablePortal
								anchorEl={state.menuAnchorEl}
								open={state.menuAnchorEl}
								onClose={handleCloseMenu}
							>
								<MenuItem onClick={printChart}>{strings.Print}</MenuItem>
								{options.exportEnabled && (
									<MenuItem onClick={handleExportComponentAsJPEG}>Save as JPEG</MenuItem>
								)}
								{options.exportEnabled && (
									<MenuItem onClick={handleExportComponentAsPNG}>Save as PNG</MenuItem>
								)}
							</Menu>
							{Array.isArray(extraControls) &&
								extraControls.map(({ Component = Button, text, ...controlProps }, i) => (
									<Component
										type="button"
										variant="contained"
										size="small"
										disableElevation
										{...controlProps}
										key={`control-${i}`}
									>
										{text}
									</Component>
								))}
						</Box>
					</Grid>
				</Grid>
				<Grid container display={'flex'} alignItems={'center'} justifyContent={'center'}>
					<Grid size={'grow'} ref={componentRef}>
						<ResponsiveContainer
							height={600}
							margin={{
								top: 20,
								right: 20,
								bottom: 20,
								left: 20
							}}
						>
							<LineChart
								data={options.data}
								onMouseDown={handleOnChartMouseDown}
								onMouseUp={handleOnChartMouseUp}
								onMouseMove={handleOnChartMouseMove}
							>
								<CartesianGrid strokeDasharray="3 3" />
								<XAxis
									allowDataOverflow
									dataKey="eventName"
									domain={state.xAxisBoundaries}
									type="number"
									tickFormatter={options?.axisX?.valueFormatter}
								/>

								<YAxis
									yAxisId={`yAxis1`}
									label={
										<Label
											value={options.axisY?.title}
											position="center"
											angle={270}
											dx={-30}
											fontSize={`16px`}
											fontWeight="bold"
										/>
									}
								/>

								{options?.axisY?.stripLines?.length > 0 &&
									options?.axisY?.stripLines?.map(
										({ value, label, color, thickness, labelBackgroundColor }, i) => (
											<ReferenceLine
												y={value}
												stroke={color}
												strokeWidth={thickness}
												label={
													<Label
														value={label}
														position="insideTopRight"
														style={{ backgroundColor: '#000000' }}
														fill={color}
														fontWeight="bold"
													>
														{label}
													</Label>
												}
												yAxisId="yAxis1"
												key={`refLineY-${i}`}
											/>
										)
									)}
								<Tooltip labelFormatter={options.axisX?.valueFormatter} />
								<Legend
									style={{ marginTop: 20, cursor: 'pointer' }}
									content={CustomizedLegend}
									onClick={handleLegendOnClick}
									onMouseEnter={handleLegendOnMouseEnter}
									onMouseLeave={handleLegendOnMouseLeave}
								/>
								{Object.keys(seriesList)?.map((dataKey, i) => (
									<Line
										type="monotone"
										strokeWidth={2}
										stroke={
											state.selectedDataKey
												? state.selectedDataKey === dataKey
													? chartColors[i]
													: theme.palette.divider
												: chartColors[i]
										}
										key={dataKey}
										dataKey={dataKey}
										hide={state.inactiveSeries?.includes(dataKey)}
										dot={
											options?.axisY?.stripLines?.length >= 3
												? {
													fill: state.selectedDataKey
														? state.selectedDataKey === dataKey
															? chartColors[i]
															: theme.palette.divider
														: chartColors[i],
													stroke: state.selectedDataKey
														? state.selectedDataKey === dataKey
															? chartColors[i]
															: theme.palette.divider
														: chartColors[i],
													strokeWidth: 0,
													r: 3
												}
												: false
										}
										yAxisId={`yAxis1`}
									/>
								))}
								{options?.axisX?.stripLines?.length > 0 &&
									options?.axisX?.stripLines?.map(({ value, label, color, thickness }, i) => (
										<ReferenceLine
											x={options?.axisX.valueParser(value)}
											yAxisId="yAxis1"
											stroke={color}
											strokeWidth={thickness}
											label={
												<Label
													value={label}
													position="center"
													angle={270}
													dx={16}
													fill={color}
													color={color}
													fontWeight="bold"
												/>
											}
											key={`refLine-${i}`}
										/>
									))}
								<ReferenceArea yAxisId="yAxis1" strokeOpacity={0.3} {...state.referenceArea} />
							</LineChart>
						</ResponsiveContainer>
					</Grid>
				</Grid>
			</Grid>
		);
	}
);
export default memo(StockChartGraph);
